import { InfoIcon, RefreshCcwIcon } from 'lucide-react';
import { type ReactElement, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import {
  InputFile,
  type InputFileHandle,
} from 'src/components/inputs/InputFile';
import { Button } from 'src/components/ui/button';
import { Label } from 'src/components/ui/label';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from 'src/components/ui/tooltip';
import { ConfigurationCriteriaProductsRecap } from 'src/features/configuration/configurationCriteria/configurationCriteriaProducts/ConfigurationCriteriaProductsRecap';
import { useConfigurationCriteriaProducts } from 'src/features/configuration/configurationCriteria/configurationCriteriaProducts/hooks/useConfigurationCriteriaProducts';
import { useScopePurchaseProductsQuery } from 'src/services/api/getScopePurchaseProducts';
import { useSynchronizeProductWithScopePurchase } from 'src/services/api/syncronizeProductWithScopePurchase';
import { useBoundStore } from 'src/store/store';

export function ConfigurationCriteriaProducts(): ReactElement {
  const { t } = useTranslation('configuration');
  const inputFileRef = useRef<InputFileHandle>(null);
  const { validSkus, invalidSkus } = useBoundStore();
  const { data: scopePurchaseProducts } = useScopePurchaseProductsQuery();
  const {
    open,
    setOpen,
    handleFileUpload,
    loading,
    handleSubmit,
    handleCancel,
  } = useConfigurationCriteriaProducts(scopePurchaseProducts, inputFileRef);
  const { mutate: synchronizeProductWithScopePurchase, isPending } =
    useSynchronizeProductWithScopePurchase();

  return (
    <div className='grid w-full max-w-sm items-center gap-3'>
      <Label className='cursor-pointer' htmlFor='products'>
        {t('Manual import of products')}
      </Label>
      <div className='flex items-center gap-2'>
        <InputFile
          accept='.csv'
          disabled={loading || isPending}
          id='products'
          onFileUpload={handleFileUpload}
          ref={inputFileRef}
        />

        <Button
          className='px-2'
          onClick={() => synchronizeProductWithScopePurchase()}
          size='icon'
          variant='outline'
        >
          <RefreshCcwIcon className='size-4' />
        </Button>
        <Tooltip>
          <TooltipTrigger>
            <InfoIcon className='size-4' />
          </TooltipTrigger>
          <TooltipContent className='max-w-xs'>
            <p className='flex flex-col gap-1 font-normal text-sm'>
              <span>
                {t(
                  'This button allows to synchronize the products with the scope purchase',
                )}
              </span>
              <span>
                {t(
                  'There is already a task that updates the products every day at midnight',
                )}
              </span>
            </p>
          </TooltipContent>
        </Tooltip>
      </div>
      <ConfigurationCriteriaProductsRecap
        invalidSkus={invalidSkus}
        onCancel={handleCancel}
        onOpenChange={setOpen}
        onSubmit={handleSubmit}
        open={open && (validSkus.length > 0 || invalidSkus.length > 0)}
        validSkus={validSkus}
      />
    </div>
  );
}
